/**
 * Selection rules shared by the two outbound placement-backfill runners: the
 * admin dashboard job (src/adminApp/layout/adminPlacementBackfill.js, over
 * AppSync) and the operations script
 * (scripts/backfill-outbound-placement-time.js, direct DynamoDB). Written in
 * CommonJS so the Node script can require it — keep ESM syntax out.
 */

/**
 * Statuses that mean the load was actually placed (spotted). Deliberately an
 * allowlist: everything else — NEW, the ADVICE_* advice states, PENDING — is a
 * shipment nobody has placed yet, whose arrivalDateTime therefore cannot be a
 * legacy placed time.
 */
const PLACED_STATUSES = ["ARRIVED", "PLACED", "LOADED", "RELEASED"];

/** The outbound by-dates composite sort attribute the ARRIVED->PLACED flip
 * must rewrite alongside status. */
const DATES_KEY =
  "status#endDateTime#arrivalDateTime#scheduledDateTime#createDateTime";

/**
 * Transformer-v1 composite @key composition rule: fields joined with "#",
 * null/absent members rendered as the resolver's unresolved reference text
 * (verified against live rows; see driverApi lib/compositeKeys.js). Only the
 * direct-DynamoDB script needs this — the AppSync update resolver recomposes
 * the synthetic attributes itself.
 */
const composeKey = (fields, item) =>
  fields
    .map((f) => (item[f] != null ? item[f] : "${ctx.args.input." + f + "}"))
    .join("#");

/**
 * A record needs the backfill when it has no placementDateTime, has an
 * arrivalDateTime, and is already past PENDING.
 *
 * The status test is what keeps a driver gate check-in from being recorded as
 * a placement: a PENDING shipment with an arrival time has a driver at the
 * gate, not a placed load. Post-release the check-in itself advances PENDING
 * to ARRIVED, making a new-semantics ARRIVED row indistinguishable from a
 * legacy placed one by status alone — but only the check-in writes
 * gateCheckInId, so an ARRIVED row that carries one is a gate arrival and is
 * skipped. That test is only a prefilter, though: terminal check-ins null the
 * shipment's gateCheckInId, so every remaining ARRIVED candidate must also
 * pass an existence check against the GateCheckIn table before it may flip
 * to PLACED.
 */
const needsPlacementBackfill = (record) =>
  Boolean(
    record &&
      !record.placementDateTime &&
      record.arrivalDateTime &&
      PLACED_STATUSES.includes(record.status) &&
      !(record.status === "ARRIVED" && record.gateCheckInId),
  );

module.exports = {
  PLACED_STATUSES,
  DATES_KEY,
  composeKey,
  needsPlacementBackfill,
};
